/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  Switch,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  CustomIconButton,
  CustomTooltip,
  FilterChip,
  FilterOption,
} from '@opengeoweb/shared';
import { Close, Options } from '@opengeoweb/theme';
import type {
  PickedFilterState,
  OptionKey,
  UpdateFilter,
} from './filter-hooks';
import { warningColors } from './filter-styles';
import { emptyFilterId, getSelectedOptions } from './filter-utils';
import { useWarningsTranslation } from '../../utils/i18n';
import { getTranslationKey } from '../../utils/warningUtils';
import { getLevelStyle } from '../PublicWarningsForm/LevelField';
import { getPhenomenaIcon } from '../PublicWarningsForm/PhenomenonField';

type IdType = Exclude<keyof PickedFilterState, 'time'>;
export interface ExpandableFilterChipProps {
  id: IdType;
  options: PickedFilterState[IdType];
  updateFilter: UpdateFilter;
}

const ExpandableFilterChip: React.FC<ExpandableFilterChipProps> = ({
  id,
  options,
  updateFilter,
}: ExpandableFilterChipProps) => {
  const { t } = useWarningsTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { noSelectedOptions, allSelected } = getSelectedOptions(options);
  const isDisabled = Object.keys(options).length < 2;

  return (
    <>
      <CustomTooltip
        placement="bottom"
        title={isDisabled ? t('filter-no-results') : t('filter-options')}
      >
        <span style={{ margin: 0 }}>
          <FilterChip
            actionIcon={
              <Grid
                container
                style={{
                  marginRight: '8px',
                  minWidth: '36px',
                }}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Options />
                <Typography sx={{ paddingLeft: 0.5 }} variant="body2">
                  {!isDisabled && noSelectedOptions > 0 && noSelectedOptions}
                </Typography>
              </Grid>
            }
            id={id}
            isDisabled={isDisabled}
            isSelected={!isDisabled && noSelectedOptions > 0}
            label={t(`filter-${id}`)}
            onClick={(ref: HTMLElement | null) => setAnchorEl(ref)}
            onClickIcon={(ref: HTMLElement | null) => setAnchorEl(ref)}
            size="medium"
          />
        </span>
      </CustomTooltip>
      <Menu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        slotProps={{ backdrop: { style: { opacity: 0, transition: 'none' } } }}
        sx={{ padding: 3 }}
      >
        <DialogTitle
          id="dialog-title"
          sx={{
            zIndex: 1,
            padding: '8px 18px 16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            component="span"
            data-testid="customDialog-title"
            sx={{
              fontSize: '20px',
              fontWeight: 500,
              paddingRight: 1,
            }}
          >
            {t(`filter-${id}`)}
          </Typography>
          <CustomIconButton
            aria-label={t('close-dialog-confirm')}
            onClick={() => setAnchorEl(null)}
            sx={{
              '&.MuiIconButton-root': {
                color: 'geowebColors.greys.accessible',
              },
            }}
          >
            <Close />
          </CustomIconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '0px 24px 16px 24px',
          }}
        >
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {t('filter-select-all')}
            <Switch
              checked={allSelected}
              onChange={() => updateFilter(id, 'ALL', !allSelected)}
              slotProps={{ input: { 'aria-label': t('filter-select-all') } }}
              sx={{ marginRight: '-12px', marginTop: '-6px' }}
            />
          </Grid>
          <Box>
            {options[emptyFilterId as OptionKey<keyof PickedFilterState>] !==
              undefined && (
              <FilterOption
                checked={
                  options[emptyFilterId as OptionKey<keyof PickedFilterState>]
                }
                key={`${id}-${emptyFilterId}`}
                label={
                  <>
                    {t(
                      getTranslationKey(
                        emptyFilterId as OptionKey<keyof PickedFilterState>,
                        id,
                      ),
                    )}
                  </>
                }
                labelStyle={{
                  fontSize: '16px',
                }}
                onClick={(checked, only) =>
                  updateFilter(
                    id,
                    emptyFilterId as OptionKey<keyof PickedFilterState>,
                    checked,
                    only,
                  )
                }
                optionId={emptyFilterId}
              />
            )}
            {Object.keys(options).map((optionId) => {
              if (optionId === emptyFilterId) {
                return null;
              }
              const warningColor = warningColors[optionId as string];
              return (
                <FilterOption
                  checked={
                    options[optionId as OptionKey<keyof PickedFilterState>]
                  }
                  key={`${id}-${optionId}`}
                  label={
                    <CustomTooltip
                      placement="bottom"
                      title={t(
                        getTranslationKey(
                          optionId as OptionKey<keyof PickedFilterState>,
                          id,
                        ),
                      )}
                    >
                      <Box
                        data-testid={`wrapper-${optionId}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          maxWidth: 200,
                        }}
                      >
                        {getPhenomenaIcon(optionId)}

                        <Typography
                          noWrap
                          sx={{
                            fontSize: '16px',
                          }}
                          variant="body2"
                        >
                          {t(
                            getTranslationKey(
                              optionId as OptionKey<keyof PickedFilterState>,
                              id,
                            ),
                          )}
                        </Typography>
                      </Box>
                    </CustomTooltip>
                  }
                  labelStyle={{
                    ...getLevelStyle(warningColor),
                    width: warningColor ? 'fit-content' : '100%',
                    fontSize: '16px',
                  }}
                  onClick={(checked, only) =>
                    updateFilter(
                      id,
                      optionId as OptionKey<keyof PickedFilterState>,
                      checked,
                      only,
                    )
                  }
                  optionId={optionId}
                />
              );
            })}
          </Box>
        </DialogContent>
      </Menu>
    </>
  );
};

export default ExpandableFilterChip;
